/**
 * DD-ATTR-PRECEDENCE — single wire shape for NP Complete Triage.
 *
 * The server (DesignDevelopmentWorkflowController::completeStep, NP tile) requires
 * install_required + deliverables (NP-TRIAGE-PERSIST, 2026-08-11) and refuses the
 * price / payload_slot keys inside the attribute bags (DD-ATTR-PRECEDENCE G1). Every
 * POST …/pm_assessment/complete for a New Product line is built through this helper
 * so a contract change cannot drift one surface. Today the Full Request PMA form is the
 * only surface allowed to complete NP Triage — see NP_TRIAGE_COMPLETE_NEEDS_FORM.
 */

import type { LinePart } from '@/lib/dd-queue/lineParts'
import type { ChangeIntent } from '@/lib/dd-queue/types/subjectMutation'

/** Keys refused by DesignDevelopmentWorkflowController NP triage attr bags. */
export const NP_TRIAGE_FORBIDDEN_ATTR_KEYS = new Set(['price', 'payload_slot'])

/**
 * Why a generic "Mark complete" must not finish NP Triage: the step carries decisions
 * (install, deliverables, attribute corrections) that only the Triage form shows.
 * Shown as the toast when a surface without the form still calls the store.
 */
export const NP_TRIAGE_COMPLETE_NEEDS_FORM =
  'Complete Triage from the Full Request Triage form — it needs your install, deliverable and attribute decisions.'

export function stripNpTriageForbiddenAttrKeys(
  bag: Record<string, string> | null | undefined
): Record<string, string> {
  const out: Record<string, string> = {}
  if (bag == null) return out
  for (const [k, v] of Object.entries(bag)) {
    if (NP_TRIAGE_FORBIDDEN_ATTR_KEYS.has(k)) continue
    out[k] = v
  }
  return out
}

export type NpTriageCompleteWireFields = {
  expectedVersion: number | null
  estimationMode: string | null
  installRequired: boolean
  deliverables: Record<string, boolean>
  lineAttributes: Record<string, string>
  lineAttributeBaseline: Record<string, string>
  /** F-099 — the parts list; null = not sent (stored list untouched). */
  lineParts: LinePart[] | null
  /** F-144 (slice 3a) — "What's changing" (marked items); null = not sent (stored list untouched). */
  changeIntents: ChangeIntent[] | null
}

/**
 * Build Complete Triage fields for postDdWorkflowComplete from the Triage form state.
 * price + payload_slot are stripped from both bags (same refusal as the server).
 */
export function buildNpTriageCompleteWireFields(args: {
  expectedVersion: number | null
  estimationMode: string | null | undefined
  installRequired: boolean
  deliverables: Record<string, boolean>
  lineAttributes: Record<string, string> | null | undefined
  lineAttributeBaseline: Record<string, string> | null | undefined
  lineParts?: LinePart[] | null
  changeIntents?: ChangeIntent[] | null
}): NpTriageCompleteWireFields {
  return {
    expectedVersion: args.expectedVersion,
    estimationMode: args.estimationMode ?? null,
    installRequired: args.installRequired,
    deliverables: { ...args.deliverables },
    lineAttributes: stripNpTriageForbiddenAttrKeys(args.lineAttributes),
    lineAttributeBaseline: stripNpTriageForbiddenAttrKeys(args.lineAttributeBaseline),
    lineParts: args.lineParts ? args.lineParts.map((p) => ({ ...p })) : null,
    changeIntents: args.changeIntents ? args.changeIntents.map((i) => ({ ...i })) : null,
  }
}
